import { ID } from "./../../../../types/index";
import { ChoiceConfig } from "../../../../components/PollNewUserComponents/pollConfig";
import { ReviewsSetOrderByIdQuery } from "../../../../generated/graphql";
import { CartDataProductsItem, CartDataState } from "../../../../providers/cartDataStore";
import { SupaCategory, SupaProducts } from "./supabaseFragments";
import { SupaPageItems, SupaPromo, SupaRecipe } from "../../../../parhato2/src/shared/types/supabaseFragments";
import { IonInput } from "@ionic/react";
import { ComponentProps } from "react";

export type MarketDeliveryType = "market" | "express";

export type Banner = {
  id: string;
  imageSrc: string;
  type?: string | null;
  link?: string | null;
};

export type CategoriesMapObj = {
  allCategories: Category[];
  subCategories: Category[];
  mainCategories: Category[];
};

export type ExpressPoint = {
  id: string;
};

export type ExpressPointsData = {
  expressPoints: ExpressPoint[];
};

export type Category = {
  id: string;
  description?: string;
  image?: ImageType;
  name: string;
  parent?: string;
  count?: number;
};

export type ProductAttribute = {
  id: number | string;
  name: string;
  options?: string[];
  option?: string;
  position?: number;
  visible?: boolean;
  variation?: boolean;
};

export type ProductDefaultAttribute = {
  id: number | string;
  name: string;
  option: string;
};

export enum ProductType {
  SIMPLE = "simple",
  VARIABLE = "variable",
  VARIATION = "variation",
}

export enum ProductImageTypes {
  base64 = "base64",
  img = "img",
}

export type ImageType = {
  alt?: string;
  id?: string | number;
  name?: string;
  src: string;
  type?: keyof typeof ProductImageTypes;
  base64?: string;
  previewSrc?: string;
};

export type SupaImage = {
  id?: number;
  alt?: string;
  src: string;
  name?: string;
  preview_src?: string;
};

export type Product = {
  id: string;
  name: string;
  price: string;
  onSale: boolean;
  type: ProductType;
  stockStatus: string;
  description: string;
  attributes: ProductAttribute[];
  categories: { id: string; name: string; slug: string }[];
  stockQuantity: number;
  parentId?: string;
  averageRating?: string;
  purchasable: boolean;
  images: ImageType[];
  priceHtml?: string;
  soldIndividually: boolean;
  variations?: string[];
  defaultAttributes?: ProductDefaultAttribute[];
  groupedProducts?: string[];
  dateCreated?: string;
  isHit?: boolean;
  isNew?: boolean;
  discountPrice: string | null;
  weight?: string;
  videoUrl?: string;
  regularPrice?: string;
  portionUnit?: string;
  percentFat?: string;
  fats?: string;
  grammage?: string;
  additionalFeature?: string;
  typeOfPackaging?: string;
  storageConditions?: string;
  productType?: string;
  brand?: string;
  composition?: string;
  energyValue?: string;
  carbohydrates?: string;
  proteins?: string;
  sku?: string;
  parentName?: string;
};

export type ProductReview = {
  dateCreated: string;
  id: string;
  review: string;
  rating: number;
  fullName: string;
  status?: string;
};

type Contacts = {
  title: string;
  contact: string;
  url: string;
};

export type PageName = "common" | "fortime" | "parhato2";

export interface OrderData {
  deliveryIntervals: DeliveryInterval[];
  blockedIntervals?: BlockedInterval[];
  shippingZones: ShippingZone[];
  shippingMethods: ShippingMethod[];
  paymentMethods: PaymentMethod[];
  shippingZoneMethods: ShippingZoneMethod[];
}

export interface BlockedInterval {
  deliveryIntervalId: string;
  dayOfWeek?: number | null;
  date?: string;
}

export interface ShippingZoneMethod {
  id: number;
  shippingMethodId: number;
  shippingZoneId: number;
}

export type AppSettings = {
  supaBaseMediaUrl?: string;
  stepForWeightProductQuantity: number;
  isUnpaidOrdersBannerEnabled?: boolean;
  isUnreviewedOrdersBannerEnabled?: boolean;
  isCashGivenEnabled?: true;
  isRemoteWishlistEnabled?: boolean;
  isOrderChatEnabled: boolean;
  isSharedCartEnabled?: boolean;
  isTaxiMasterEnabled?: boolean;
  isWaysetEnabled?: boolean;
  waysetQuoteAutoConfirmAmount?: number;
  waysetQuoteAutoConfirmPercent?: number;
  isUpdateNotificationEnabled?: boolean;
  orgName?: string;
  ogrn?: string;
  innKpp?: string;
  legalAddress?: string;
  director?: string;
  offertaUrl?: string;
  defaultShippingZoneId?: string;
  isNotificationsPageEnabled?: boolean;
  notifyPaymentText?: string;
  isProductSubscriptionsEnabled?: boolean;
  orderTrackingEnabled?: boolean;
  isInviteePurchasesEnabled?: boolean;
  staticOrderData?: OrderData;
  showParentName: boolean;
  shareProduct: boolean;
  appUrl?: string;
  showStockStatus: boolean;
  expressAllowedTimeStart: string;
  expressAllowedTimeEnd: string;
  isCartSyncEnabled: boolean;
  maxVisibleProductsOnHome?: number;
  inviteFriendDesc?: string;
  inviteFriendTerms?: string;
  isReferralBannerEnabled?: boolean;
  maxWeightForOrderKG?: number;
  extraWeightFee?: number;
  isWeightFeeEnabled: boolean;
  maxWeightForFeeKG: number;
  maxWeightFeeStep: number;
  exitAppICantGoBack: boolean;
  moduleOnInit?: "CORE" | "PARHATO2";
  staticOrderDataEnabled: boolean;
  isFCMNotificationsEnabled: boolean;
  isLocalNotificationsEnabled: boolean;
  isToggleSwitchModuleEnabled: boolean;
  isImageInCartStoreEnabled: boolean;
  isShouldRecalculatePriceOnReplace: boolean;
  imageUrlPrefix?: string;
  homeSliderAutoplaySeconds?: number;
  showUnavailableProductsModal: boolean;
  isCartProgressButtonEnabled: boolean;
  isCartQuantityInputEnabled: boolean;
  isWeightProductQuantityStepEnabled: boolean;
  haveTabs: boolean;
  isEmailPushEnabled: boolean;
  isSettingsPageEnabled: boolean;
  isOffertaPageEnabled?: boolean;
  pages: {
    authPageName: PageName;
    homePageName: PageName;
    searchMainCategoriesPageName: PageName;
    myOrdersPageName: PageName;
    cartPageName: PageName;
    myAccountPageName: PageName;
    thankYouPageName: PageName;
    subCategoriesPageName: PageName;
    orderDetailPageName: PageName;
    wishlistPageName: PageName;
    productDetailPageName: PageName;
  };
  isInviteEnabled: boolean;
  isCardsEnabled: boolean;
  isPaymentsEnabled: boolean;
  isStoriesEnabled: boolean;
  customerSurvey: {
    allowDismiss: boolean;
    isEnabled: boolean;
  };
  isUnavailableProductsEnabled: boolean;
  isNecessaryToCreateAddress: boolean;
  headquarters: number[];
  hasClientToDeleteUser: boolean;
  defineSiteActivity?: string;
  aboutCompanyHtml?: string;
  contacts: Contacts[];
  requisites: string[];
  appIosId: string;
  baseUrl: string;
  webUrl?: string;
  CDNDomain: string;
  contactUsPage?: string | TrustedHTML;
  wishListIcons: boolean;
  cardProduct: boolean;

  sharedData: {
    title: string;
    text: string;
    iosUrl: string;
    androidUrl: string;
  };

  minimumOrderAmount: number;
  showNewVersionAlert: boolean;
  forceToGoNewVersion?: boolean;
  newVersionAlertTitle?: string;
  newVersionAlertMessage?: string;
  newVersionAlertButtonText?: string;
  minVersionAndroid?: string;
  minVersionIOS?: string;
  latestAndroidVersion?: string;
  latestIosVersion?: string;
  showProductReviews: boolean;
  shouldCheckItemsAvailability: boolean;
  showCouponsInCart: boolean;
  orderHistoryPagination: boolean;

  isDeliveryDateEnabled: boolean;
  isDeliveryIntervalEnabled: boolean;
  isBonusPointsEnabled: boolean;
  isReferralEnabled: boolean;
  isReferralStatsEnabled: boolean;
  isAdditionalPageEnabled: boolean;

  deleteAccountLink: string;
  showSendCustomerRequestButton: boolean;
  isCustomerAdressesEnabled: boolean;
  updateUserForm: {
    isUpdatePasswordEnabled: boolean;
    showPhoneNumberInput: boolean;
  };
  isOnlinePaymentEnabled: boolean;
  privacyPolicyPageLink: string;
  isSeoOnHomePageEnabled: boolean;
  companyPhoneNumber: string;
  isContentInFooterShown: boolean;
  hasCardLoyalty: boolean;
  showSliderCatalogButton: boolean;

  addressSettings: {
    showCustomerAddresses: boolean;
    showFlatDelivery: boolean;
    addressInputDependsOnShippingMethod: boolean;
  };

  homePageCategories: {
    customersChoice: {
      title: string;
      isEnabled: boolean;
    };
    featured: {
      title: string;
      isEnabled: boolean;
    };
    sale: {
      title: string;
      isEnabled: boolean;
    };
    latest: {
      title: string;
      isEnabled: boolean;
    };
  };
  showOrderPageProductCardCounter: boolean;
  cartMinAmountFromMethods: boolean;
  siteDomain: string;

  signUpPage: {
    nameFieldEnabled: boolean;
    lastNameFieldEnabled: boolean;
  };

  loginPage: {
    phoneOrEmailFieldEnabled: boolean;
  };
  notifications?: {
    cartRemindDelayMinutes: number;
    isOrderNotificationsEnabled?: boolean;
    isDiscountNotificationsEnabled?: boolean;

    orderNotifications?: {
      isPushEnabled?: boolean;
    };

    discountNotifications?: {
      isPushEnabled?: boolean;
      isEmailEnabled?: boolean;
      isSmsEnabled?: boolean;
    };
  };

  orderDetailPage: {
    reviewOrderEnabled: boolean;
    repeatOrderEnabled: boolean;
    cancelOrderEnabled?: boolean;
  };
  image: {
    isDownSizeEnabled: boolean;
    isLazyLoadingEnabled: boolean;
    base64Extension: string;
    isNeedBase65Prefix: boolean;
    isBase64InSrc: boolean;
  };

  shipping: {
    showOldAddressInputOnShippingPage: boolean;
    showCurrentShippingAddressSelect: boolean;
    isShippingZonesEnabled: boolean;
    showDefaultShippingZone: boolean;

    zones?: ShippingZone[];
    methods?: ShippingMethod[];
  };
  expressMaxWeightKg: number;
  isExpressDeliveryEnabled: boolean;
  newProductDuration: number;
  showStockQuantity: boolean;
  lowStockThresholdToShowPercent: number;
  minLowStockThresholdToShowNumber: number;
  search: {
    minSearchLength: number;
    inputDebounceTimeMs: number;
  };
  usePriceHTML?: boolean;
  ignoredProductAttributes?: string[];
  liveUpdate?: {
    version: string;
    url: string;
    enabled: boolean;
  };
  appMetrica?: {
    enabled: boolean;
    apiKey: string;
  };
  telegramAuth?: {
    enabled: boolean;
    botUsername: string;
  };
};

export enum OrderPaymentStatus {
  PAYED = "PAYED",
  FAILED = "FAILED",
  WAITING_PAYMENT = "WAITING_PAYMENT",
}

export type TOrder = {
  id: string | number;
  total: number;
  number?: string;
  status?: string;
  paymentStatus?: OrderPaymentStatus | string;
  lineItems?: LineItem[];
  dateCreated?: string;
  shippingLine?: ShippingLine;
  currencySymbol?: string;
  discountTotal?: string;
  shippingTotal?: string;
  customerNote?: string;
  deliveryDate?: Date;
  deliveryTime?: string;
  paymentMethod?: PaymentMethod;
  customer?: {
    whatsappNumber?: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    id?: string;
  };
  address?: Address;
  driverRating?: any;
  rewardPoints?: number;
  coupons?: Partial<Coupon>[];
  needsPayment?: boolean;
  statusName?: string;
  isExpressDelivery?: boolean;
  pickerListPrinted?: boolean;
  payments?: Payments;
  cash_given?: number | null;
  weight?: number | null;
};

export type ProductsQueryParams = {
  order?: string;
  orderby?: string;
  featured?: boolean;
  on_sale?: boolean;

  saleType?: SaleTypeEnum;
  categoryId?: string | number;
  pageSize?: string;
  pageNumber?: string;
  sortOrder?: string;
  minPrice?: string;
  maxPrice?: string;
  keyword?: string;
  expressPointId?: string;
  zoneId?: string;
  additionalProductFilter?: string;
  type?: string;
  productIds?: string[];
};

export type SearchProductsQueryParams = {
  pageNumber: number;
  keyword: string;
  additionalFeature?: string;
};

export type User = {
  id: string;
  token: string;
  firstName: string;
  lastName: string;
  userName?: string;
  email?: string;
  phone?: string;
  discountCardNumber?: string;
  fcmToken?: string;
  supabaseAuth?: {
    accessToken: string;
    refreshToken: string;
  };
};

export type UserWithPassword = User & {
  password?: string;
};

export type LoginVariables = {
  username: string;
  password: string;
};

export type SmsAuthVariables = {
  phone: string;
  verificationCode: string;
  verificationCodeId: string;
  firstName?: string;
  lastName?: string;
  referralCode?: string;
};

export type ResetPasswordVariables = {
  email?: string;
  phone?: string;
  newPassword?: string;
  verificationCode?: string;
  verificationCodeId?: string;
};

export type VerifySMSCodeVariables = {
  code: string;
  phoneNumber: string;
  codeID: string;
};

export type RegisterUserCredentials = {
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  referal?: {
    referralLink: string;
    ownVersionReferral: string;
  };
  verification?: {
    verificationCode: string;
    verificationCodeId: string;
  };
};

export type RegisterResponse = {
  userName: string;
};

export type VerificationCodeResponse = {
  verificationCodeId: string;
  phoneNumber: string;
};

export type TTaxiShippingWarning = {
  code?: string;
  message: string;
  delayMin?: number;
  delayMax?: number;
};

export type TTaxiShippingQuote = {
  total: number | null;
  warnings: TTaxiShippingWarning[];
  quoteToken?: string;
  quoteValidForSeconds?: number;
  quoteExpiresAt?: string | null;
  conditionsVersion?: string;
};

export enum ShippingMethodType {
  flatRate = "flat_rate",
  freeShipping = "free_shipping",
  taxiShipping = "taxi_shipping",
  pickupPoint = "pickup_point",
  courier = "courier",
  taxiMaster = "taxi_master",
}

export enum PaymentMethodIdEnum {
  cash = "cash",
  card = "card",
}

export enum SaleTypeEnum {
  featured = 1,
  on_sale = 2,
}

export enum ShippingMethodDeliveryType {
  default = "DEFAULT",
  nextDay = "NEXT_DAY",
}

export type ShippingMethod = {
  id: string;
  enabled: boolean;
  description: string;
  title: string;
  minAmount?: string;
  cost?: string;
  methodId: string;
  methodDeliveryType: ShippingMethodDeliveryType;
  methodType: ShippingMethodType;
  order?: number;
  minDeliveryDays?: number;
  maxDeliveryDays?: number;
};

export type PaymentMethod = {
  id: string;
  title: string;
  enabled?: boolean;
  type: PaymentMethodIdEnum;
};

export type ShippingZone = {
  id: string;
  name: string;
};

export type ShippingLine = {
  methodId: string;
  methodTitle: string;
  shipId: string | number;
  total: string;
  methodType?: ShippingMethodType;
};

export type PickupPoint = {
  id: string;
  name: string;
  address: string;
  hours?: string;
  latitude: string;
  longitude: string;
};

export type Address = {
  id?: string;
  name?: string;
  state?: string;
  city?: string;
  street?: string;
  apartmentNumber?: string;
  fullAddress?: string;
  entrance?: string;
  floor?: string;
  house?: string;
  intercom?: string;
  isFlat?: boolean;
  latitude?: string;
  longitude?: string;
  expressPointId?: string;
};

export type AddressInput = Omit<Address, "id" | "name" | "fullAddress" | "expressPointId"> & {};

export type AddressSuggestion = {
  text: string;
  country: string;
  province: string;
  area: string;
  locality: string;
  district: string;
  street: string;
  house: string;
  point: string;
  location?: number[];
  postcode: string;
};

export type LocationAddress = {
  latitude: string;
  longitude: string;
};

export type CheckForExpressPayload = {
  latitude: string;
  longitude: string;
  totalWeight: number;
};

export type CreateOrderData = Omit<TOrder, "id" | "number" | "paymentMethod" | "shippingLine"> &
  Required<Pick<TOrder, "paymentMethod" | "shippingLine">>;

export interface LineItem {
  id: string;
  quantity: number;
  variationId?: string;

  name?: string;
  image?: ImageType;
  productId?: Product["id"];
  total?: string;
  subtotal?: string;
  price?: string;
  sku?: string;
  portion_unit?: string;
  unitName?: string;
  parentName?: string;
  // TODO:AlievIB: temporary solution (I hope it will be fixed in the future)
  attributes?: ProductAttribute[];
}

export type CreatePaymentPayload = {
  amount: number;
  orderNumber: string;
  orderId: string;
};

export type PaymentFormData = {
  orderId: string;
  formUrl: string;
};

export interface DeliveryInterval {
  id: string;
  text: string;
}

export enum CouponDiscountType {
  PERCENT = "percent",
  FIXED_CART = "fixed_cart",
  FIXED_PRODUCT = "fixed_product",
}

export type Coupon = {
  id: string;
  code: string;
  discountType: CouponDiscountType;
  discountAmount: number;
  dateExpires?: string;
  minimumAmount?: number;
  maximumAmount?: number;
  individualUse?: boolean;
  productIds?: number[];
  excludedProductIds?: number[];
  freeShipping?: boolean;
  productCategories?: number[];
  excludedProductCategories?: number[];
  excludeSaleItems?: boolean;
};

// YYYY-MM-DD string
export type ISODate = `${number}-${number}-${number}`;

export type ReviewSet = {
  reviewSet: ReviewsSetOrderByIdQuery["customerOrderById"]["reviewSet"]["edges"][number]["node"];
  orderNumber: ReviewsSetOrderByIdQuery["customerOrderById"]["orderNumber"];
};

// TODO: Refactor adresses
export type ParhatoAddressObject = {
  text: string;
  country: string;
  province: string;
  area: string;
  locality: string;
  district: string;
  street: string;
  house: string;
  point: string;
  location?: number[];
  postcode: string;
};

export type RemoveUserResponse = { success: boolean } | null;

export type SurveyAnswers = {
  city: string;
  fullName: string;
  brandName: string;
  storeName: string;
  phoneNumber: string;
  companyName: string;
  deliveryAddress: string;
  establishmentName: string;
  purchaseResponsible: string;
  establishmentCount: string;
  position: string;
  multipleEstablishments: string;
  establishmentType: "Кондитерская" | "Ресторан" | "Кофейня" | string;
  choice: Array<ChoiceConfig["id"]>;
  payment: string;
  establishmentCharacteristics:
    | "Заведение с посадочными местами"
    | "Заведение с витриной для продаж"
    | "Подходит оба варианта"
    | "";
};

export type ProductReviewPayload = Omit<ProductReview, "id" | "dateCreated"> & {
  reviewer: string;
  product_id: number | string;
  reviewer_email: string;
};

export type OrderReviewType = {
  orderId: string;
  productQuality: number;

  deliveryQuality?: number | null;
  appUsability?: number | null;
  /** @deprecated legacy fields, kept for backward compatibility */
  assemblyQuality?: number;
  deliverySpeed?: number;
  driverCourtesy?: number;
  customerNote?: string | null;
};

export type StorySlide = {
  id: number;
  src: string;
  type: "image" | "video";
  button:
    | {
        role: "route" | "_blank";
        text: string;
        link: string;
      }
    | undefined;
};

export type StoryType = {
  id: number;
  name: string;
  thumbnailURL: string;
  content: StorySlide[];
};

export type CartProduct = CartDataProductsItem;

export type CartState = CartDataState;

export type RawCartResponse = {
  total: number;
  user_id?: string;
  created_at?: Date;
  updated_at: Date;
  reward_points: number;
  coupons: CartState["coupons"];
  products: CartState["cartProducts"];
  sold_out_products: CartState["soldOutProducts"];
  unavailable_products: CartState["unavailableProducts"];
};

export type Card = {
  cardFirstSix: string;
  cardLastFour: string;
  cardType: string;
  dataCreated: string;
  dataUpdated: string;
  id: ID;
  paymentMethodId: string;
  userId: ID;
};

export type DeletedCard = {
  success: Boolean;
  deletedId: ID;
};

export type CreateYooKassaPaymentInput = {
  amount: number;
  orderId: string;
  profileId: string;
  description?: string;
};

export type PayWithSavedMethodInput = {
  amount: {
    value: string;
    currency: string;
  };
  payment_method_id: string;
  description?: string;
  metadata?: {
    profile_id: string;
    order_id: string;
  };
};

export type GetPaymentStatusInput = {
  paymentId: string;
};

export type PaymentMethodType = "sbp" | "sberpay" | "tpay" | "card";

export type PaymentStatusType = "pending" | "succeeded" | "failed" | "canceled" | null;

export type CreateYooKassaPaymentEF = {
  amount: {
    value: number;
    currency: string;
  };
  confirmation: {
    type: string;
  };
  capture: boolean;
  description: string;
  metadata: {
    order_id: string;
    profile_id: string;
  };
  merchant_customer_id: string;
};

export type ORDERS_PARAMS = {
  page: number;
  customer: string;
};

export type ORDERS_HISTORY_PARAMS = {
  page: number;
  per_page: number;
};

export type PageItem = {
  id: string;
  meta?: any[];
  type: string;
  page: string;
  tag?: string;
  order: number;
  title?: string;
  createdAt: string;
  categoryId: string | null;
  productIds?: string[];
  images: ImageType[];
  base64?: string;
};

type RecipeIngredient = {
  name: string;
  quantity: number;
  portionUnit: string;
};

export type Recipe = {
  id: number;
  title: string;
  image: string;
  description: string;
  productIds: Product["id"][];
  ingredients: RecipeIngredient[];
};

export interface PromoPayloadSection extends SupaPageItems {
  items: SupaPromo[];
}

export interface ProductShelvesPayloadSection extends SupaPageItems {
  items: SupaProducts[];
}

export interface SelectionsPayloadSection extends SupaPageItems {
  items: SupaProducts[][];
}

export interface CategoriesPayloadSection extends SupaPageItems {
  items: {
    parentCategory: SupaCategory | null;
    subCategories: SupaCategory[] | null;
  };
}

export interface RecipesPayloadSection extends SupaPageItems {
  type: "recipes";
  items: SupaRecipe[];
}

export type HomeDataPayloadSection =
  | PromoPayloadSection
  | ProductShelvesPayloadSection
  | SelectionsPayloadSection
  | RecipesPayloadSection
  | CategoriesPayloadSection;

export interface PromoSection extends PageItem {
  items: Promo[];
}

export interface ProductShelvesSection extends PageItem {
  items: Product[];
}

export interface SelectionsSection extends PageItem {
  items: Product[][];
}

export interface RecipesSection extends PageItem {
  items: Recipe[];
}

export interface CategoriesSection extends PageItem {
  items: {
    parentCategory: Category | null;
    subCategories: Category[] | null;
  };
}

export type HomeDataSection =
  | PromoSection
  | ProductShelvesSection
  | SelectionsSection
  | RecipesSection
  | CategoriesSection;

export type Promo = {
  id: string;
  title: string;
  category?: Category | null;
  productIds?: Product["id"][];
  order: number;
  description?: string;
  images: ImageType[];
  link_type?: "internal" | "external" | null;
  link_value?: string | null;
};

export type OrderHistoryProductImage = {
  productId: number;
  images: ImageType[];
};

export type OrderHistory = {
  id: string;
  total: string;
  status: string;
  statusName: string;
  customerId: string;
  deliveryDate: string;
  paymentStatus: string;
  createdAt: string;
  productImage: OrderHistoryProductImage[];
  shippingTotal: number;
  isExpressDelivery: boolean;
  rewardPoints: number;
  itemsCount: number;
  reviewRating?: number;
};

// TODO:: Move to file component types
export interface CartUnavailableProductProps {
  product?: Product | undefined;
  className?: string;
  removeProductCardTxt: string;
  removeProductCardTxtClassName?: string;
  cartItem: CartDataProductsItem;
  removeUnavailableProduct: (productId: Product["id"]) => void;
}

export type ProductsSearchBarHeaderProps = {
  onClean?: () => void;
  onCancel?: () => void;
  showBackButton?: boolean;
  showCancelButton?: boolean;
} & ComponentProps<typeof IonInput>;

export type NotificationsSearchBarHeaderProps = {
  onClean?: () => void;
  onCancel?: () => void;
  showBackButton?: boolean;
  showCancelButton?: boolean;
} & ComponentProps<typeof IonInput>;

export type ProductSubscription = {
  customer_id: User;
  product_id: Product;
};

export type Parhato2ProductSubscription = {
  customerId: User;
  productId: Product;
};

export type SupaAdditionalFeature = {
  additional_feature: string;
  count: number;
};

export type SupaProductResults = {
  products: SupaProducts[];
  additional_features?: SupaAdditionalFeature[];
};

export type AdditionalFeature = {
  additionalFeature: string;
  count: number;
};

export type ProductResults = {
  products: Product[];
  additionalFeatures?: AdditionalFeature[];
};

export interface InviteVariables {
  inviteePhone: string;
  referralId?: string;
}

export interface Invitation {
  id: string;
  invitee_phone: string;
  status: string;
}

export interface DeleteInvitationVariables {
  id: string;
}

export interface Invitee {
  phone: string;
  purchases: number; // Now represents total spent amount, not purchase count
  id: number;
  status: string;
}

export interface ReferralStats {
  totalPurchases: number; // Now represents total spent amount, not purchase count
  totalPointsEarned: number;
  invitees: Invitee[];
}

export type GroupedParhato2Product = {
  id: number;
  name: string;
  slug: string;
};

export type ProductDefaultAttributeParhato2 = {
  name: string;
  option: string;
  id: number | string;
};

export interface Parhato2ProductCategory {
  id: number;
  name: string;
  slug: string;
}

export type TinkoffGetQrResponse = {
  TerminalKey: string;
  OrderId: string;
  Success: boolean;
  Status: TinkoffPaymentStatus;
  PaymentId: number;
  ErrorCode: string;
  Message: string;
  Amount: number;
  Data: string;
};

export type TinkoffGetQr = {
  terminalKey: string;
  orderId: string;
  success: boolean;
  status: TinkoffPaymentStatus;
  paymentId: number;
  errorCode: string;
  message: string;
  amount: number;
  paymentURL: string;
};

export type TinkoffPaymentStatus =
  | "NEW"
  | "AUTHORIZED"
  | "CONFIRMED"
  | "PARTIAL_REVERSED"
  | "REVERSED"
  | "PARTIAL_REFUNDED"
  | "REFUNDED"
  | "REJECTED";

export type TinkoffPayment = {
  id: number;
  extra: any;
  total: number;
  orderId: number;
  customerId: number;
  createdAt: string;
  providerPaymentId: number;
  providerPaymentUrl: string;
  status: TinkoffPaymentStatus;
};

export type Notification = {
  body: string;
  createdAt: string;
  customerId: number | null;
  data: any[] | null;
  id: number;
  isRead: boolean;
  title: string;
  type: string | null;
  typeName: string | null;
  url: string | null;
  urlType: string | null;
};

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface NotificationTypesPayload {
  isPublicOnly?: boolean;
}

export interface NotificationTypeItem {
  type: string;
  typeName: string;
}

export interface NotificationTypesResponse {
  notificationTypes: NotificationTypeItem[];
}

export interface NotificationsPayload {
  type?: string;
  search?: string;
  limit?: number;
  page?: number;
  perPage?: number;
  isPublicOnly?: boolean;
}

export interface MarkAsReadParams {
  notificationId?: number;
  markAllAsRead?: boolean;
}

export interface MarkAsReadResponse {
  success: boolean;
  message: string;
  affectedCount?: number;
}

export interface NotificationQueryParams {
  page: number;
  perPage: number;
  customerId?: string;
  type?: string | null;
  search?: string;
  isPublicOnly?: boolean;
}

export interface NotificationReadRecord {
  notification_id: number;
  created_at: string;
}

export interface FetchReadRecordsParams {
  notificationIds: number[];
}

export interface FetchReadNotificationIdsParams {
  customerId: string;
}

export type NotificationWithReadStatus = Notification & {
  isRead: boolean;
  readAt?: string | null;
};

export type OrderTracking = {
  id: string;
  startLatitude: number;
  startLongitude: number;
  endLatitude: number | null;
  endLongitude: number | null;
  currentLatitude: number | null;
  currentLongitude: number | null;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  customerId: number;
  orderId: TOrder["id"];
};

export type OrderTrackingPayload = {
  orderId: TOrder["id"];
};

export type ReferralStatsResponse = {
  total_purchases: number;
  total_points_earned: number;
  invitees: Invitee[];
};

export type SavePaymentCardResponse = {
  id: string;
  status: string;
  paid: boolean;
  amount: {
    value: string;
    currency: string;
  };
  confirmation?: {
    type: "redirect";
    confirmation_url: string;
  };
  description?: string;
  metadata?: Record<string, any>;
};

export type SavePaymentCardArgs = {
  route: string;
};

export type Payments = {
  id: number;
  amount: string;
  paymentMethod: string;
  status: string;
  customerId: number;
  orderId: number | null;
  paymentId: string | null;
  confirmationUrl: string | null;
  comment: string;
  createdAt: string;
  updatedAt: string | null;
};

export type SbpPaymentArgs = {
  orderId: TOrder["id"];
  customerId: User["id"];
};

export type OrderChatStatus = "open" | "closed" | "archived";
export type OrderChatSenderType = "customer" | "operator" | "system";
export type OrderChatMessageType = "text" | "image" | "video" | "file";
export type OrderChatAttachmentStatus = "pending" | "ready" | "failed";
export type OrderChatMessageStatus = "sending" | "sent" | "failed";

export type OrderChat = {
  id: number;
  orderId: number;
  customerId: number;
  operatorAdminUserId: number | null;
  status: OrderChatStatus;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  customerLastReadAt: string | null;
  operatorLastReadAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OrderChatAttachment = {
  id: number;
  chatId: number;
  messageId: number | null;
  bucketId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  status: OrderChatAttachmentStatus;
  createdAt: string;
  signedUrl: string | null;
};

export type OrderChatMessage = {
  id: number;
  chatId: number;
  senderType: OrderChatSenderType;
  senderCustomerId: number | null;
  senderAdminUserId: number | null;
  messageType: OrderChatMessageType;
  text: string | null;
  createdAt: string;
  deletedAt: string | null;
  attachments: OrderChatAttachment[];
  clientId?: string;
  status?: OrderChatMessageStatus;
};

export type OrderChatUnreadSummaryItem = {
  chatId: number;
  orderId: number;
  lastMessageAt: string | null;
  customerLastReadAt: string | null;
  unreadCount: number;
};

export type OrderChatUnreadSummary = {
  items: OrderChatUnreadSummaryItem[];
};

export type OrderChatCursor = {
  createdAt: string;
  id: number;
};

export type OrderChatMessagesPage = {
  items: OrderChatMessage[];
  nextCursor: OrderChatCursor | null;
};

export type GetOrderChatPayload = { orderId: number };

export type GetOrderChatMessagesPayload = {
  chatId: number;
  before?: OrderChatCursor;
  limit?: number;
};

export type SendOrderChatMessagePayload = {
  chatId: number;
  text: string;
};

export type CreateOrderChatAttachmentIntentPayload = {
  chatId: number;
  file: File;
};

export type CreateOrderChatAttachmentIntentResponse = {
  attachmentId: number;
  bucket: string;
  storagePath: string;
  signedUploadUrl: string;
  token: string;
  expiresIn: number;
};

export type ConfirmOrderChatAttachmentPayload = {
  chatId: number;
  attachmentId: number;
};

export type MarkOrderChatReadPayload = { chatId: number; orderId?: number };

export type MarkOrderChatReadResult = {
  chatId: number;
  customerLastReadAt: string | null;
};

export type SharedCartProductRef = { id: Product["id"]; quantity: number };

export type SharedCart = {
  id: string;
  name: string;
  description: string;
  created_at: string;
  created_by: User["id"] | null;
  products: SharedCartProductRef[];
};

export type CreateSharedCartPayload = Omit<SharedCart, "id" | "created_at">;

export type CustomerWishlist = {
  productIds: Product["id"][];
  id: number;
};

export type ProductWishlistResponse = { success: boolean; message: string };

// ------------------------------------------------------------ \\
export interface GroupedConfig {
  /** * Общие настройки приложения */
  general: _General;
  /** * Настройки CDN, изображений и ассетов */
  assets: _Assets;
  /** * Варианты шаблонов для экранов */
  layoutVariants: _LayoutVariants;
  /** * Главная витрина, баннеры и подборки */
  showcase: _Showcase;
  /** * Каталог, поиск и карточки товаров */
  catalog: _Catalog;
  /** * Избранное */
  wishlist: _Wishlist;
  /** * Корзина */
  cart: _Cart;
  /** * Заказы и действия с заказами */
  orders: _Orders;
  /** * Авторизация и регистрация */
  auth: _Auth;
  /** * Профиль, адреса и настройки пользователя */
  users: _Users;
  /** * Доставка, зоны, интервалы и лимиты */
  delivery: _Delivery;
  /** * Оплата и способы оплаты */
  payments: _Payments;
  /** * Бонусы, рефералка и лояльность */
  loyalty: _Loyalty;
  /** * Push, email, SMS и локальные уведомления */
  notifications: _Notifications;
  /** * Контентные блоки и шаринг */
  content: _Content;
  /** * Юридическая информация и документы */
  legal: _Legal;
  /** * Внешние сервисы и SDK */
  integrations: _Integrations;
}

export interface _General {
  /** * Включает табовую навигацию */
  haveTabs: boolean;
  /** * ID приложения в App Store */
  appIosId: string;
  /** * Основной домен сайта */
  siteDomain: string;
  /** * Модуль, который инициализируется при старте */
  moduleOnInit: string;
  /** * Закрывать приложение, если нельзя вернуться назад */
  exitAppICantGoBack: boolean;
  /** * Показывать уведомление о новой версии */
  showNewVersionAlert: boolean;
  /** * URL приложения */
  appUrl: string;
  /** * Включает переключение модулей */
  isToggleSwitchModuleEnabled: boolean;
  /** * Включает уведомления об обновлении */
  isUpdateNotificationEnabled: boolean;
  /** * Координаты главного офиса */
  headquarters: number[];
  /** * Телефон компании */
  companyPhoneNumber: string;
  /** * Описание деятельности компании */
  defineSiteActivity: string;
  /** * Базовый API URL */
  baseUrl: string;
}

export interface _Assets {
  /** * Использовать base64 прямо в src изображения */
  isBase64InSrc: boolean;
  /** * Добавлять data-prefix к base64 */
  isNeedBase64Prefix: boolean;
  /** * Домен CDN */
  cdnDomain: string;
  /** * Формат base64 изображений */
  imageBase64Extension: string;
  /** * Уменьшать изображения перед показом */
  isImageDownSizeEnabled: boolean;
  /** * Включает lazy loading изображений */
  isImageLazyLoadingEnabled: boolean;
  /** * Префикс URL для изображений */
  imageUrlPrefix: string;
}

export interface _LayoutVariants {
  /** * Шаблон экрана авторизации */
  authPageName: string;

  /** * Шаблон главного экрана */
  homePageName: string;

  /** * Шаблон корзины */
  cartPageName: string;

  /** * Шаблон списка заказов */
  myOrdersPageName: string;

  /** * Шаблон экрана успешного оформления заказа */
  thankYouPageName: string;

  /** * Шаблон экрана избранного */
  wishlistPageName: string;

  /** * Шаблон личного кабинета */
  myAccountPageName: string;

  /** * Шаблон детальной страницы товара */
  productDetailPageName: string;

  /** * Шаблон детальной страницы заказа */
  orderDetailPageName: string;

  /** * Шаблон экрана подкатегорий */
  subCategoriesPageName: string;

  /** * Шаблон экрана основных категорий в поиске */
  searchMainCategoriesPageName: string;
}

export interface _Showcase {
  /** * Включает SEO-блоки на витрине */
  isSeoEnabled: boolean;

  /** * Максимальное количество товаров в блоках витрины */
  maxVisibleProducts: number;

  /** * Включает stories на витрине */
  isStoriesEnabled: boolean;

  /** * Интервал автопрокрутки слайдера в секундах */
  sliderAutoplaySeconds: number;

  /** * Показывает реферальный баннер */
  isReferralBannerEnabled: boolean;

  /** * Показывает кнопку перехода в каталог в слайдере */
  showSliderCatalogButton: boolean;

  /** * Показывает баннер неоплаченных заказов */
  isUnpaidOrdersBannerEnabled: boolean;

  /** * Показывает баннер заказов без отзыва */
  isUnreviewedOrdersBannerEnabled: boolean;

  /** * Категории и подборки товаров на витрине */
  showcaseCategories: _ShowcaseCategories;
}

export interface _ShowcaseCategories {
  /** * Блок новых товаров */
  latest: _Latest;

  /** * Блок товаров со скидкой */
  sale: _Sale;

  /** * Блок рекомендуемых товаров */
  featured: _Featured;

  /** * Блок выбора покупателей */
  customersChoice: _CustomersChoice;
}

export interface _Latest {
  /** * Название блока */
  title: string;

  /** * Включает отображение блока */
  isEnabled: boolean;
}

export interface _Sale {
  /** * Название блока */
  title: string;

  /** * Включает отображение блока */
  isEnabled: boolean;
}

export interface _Featured {
  /** * Название блока */
  title: string;

  /** * Включает отображение блока */
  isEnabled: boolean;
}

export interface _CustomersChoice {
  /** * Название блока */
  title: string;

  /** * Включает отображение блока */
  isEnabled: boolean;
}

export interface _Catalog {
  /** * Разрешает использовать HTML-разметку в цене товара */
  usePriceHTML: boolean;

  /** * Минимальная длина поискового запроса */
  minSearchLength: number;

  /** * Включает возможность поделиться товаром */
  shareProduct: boolean;

  /** * Показывает название родительского товара или категории */
  showParentName: boolean;

  /** * Показывает статус наличия товара */
  showStockStatus: boolean;

  /** * Количество дней, в течение которых товар считается новым */
  newProductDuration: number;

  /** * Показывает точное количество товара на складе */
  showStockQuantity: boolean;

  /** * Показывает отзывы о товарах */
  showProductReviews: boolean;

  /** * Задержка поиска после ввода в миллисекундах */
  searchInputDebounceTimeMs: number;

  /** * Разрешает отображать недоступные товары */
  isUnavailableProductsEnabled: boolean;

  /** * Порог остатка для показа количества в процентах */
  lowStockThresholdToShowPercent: number;

  /** * Включает подписки на товары */
  isProductSubscriptionsEnabled: boolean;

  /** * Проверяет доступность товаров перед оформлением */
  shouldCheckItemsAvailability: boolean;

  /** * Показывает модальное окно с недоступными товарами */
  showUnavailableProductsModal: boolean;

  /** * Минимальный остаток для показа количества числом */
  minLowStockThresholdToShowNumber: number;

  /** * TODO: проверить бизнес-смысл поля */
  cardProduct: boolean;
}

export interface _Wishlist {
  /** * Показывает иконки добавления в избранное */
  wishListIcons: boolean;

  /** * Хранит избранное на сервере */
  isRemoteWishlistEnabled: boolean;
}

export interface _Cart {
  /** * Минимальная сумма заказа */
  minimumOrderAmount: number;

  /** * Показывает купоны в корзине */
  showCouponsInCart: boolean;

  /** * Синхронизирует корзину с сервером */
  isCartSyncEnabled: boolean;

  /** * Включает возможность поделиться корзиной */
  isSharedCartEnabled: boolean;

  /** * Использует минимальную сумму заказа из способов доставки */
  cartMinAmountFromMethods: boolean;

  /** * Хранит изображения товаров в сторе корзины */
  isImageInCartStoreEnabled: boolean;

  /** * Сохраняет цену из корзины при синхронизации товаров, если она валидна */
  isShouldRecalculatePriceOnReplace: boolean;

  /** * Показывает progress-кнопку в корзине */
  isCartProgressButtonEnabled: boolean;

  /** * Включает ввод количества товаров через инпут */
  isCartQuantityInputEnabled: boolean;

  /** * Включает шаг 0.1 для весовых товаров (KGM) */
  isWeightProductQuantityStepEnabled: boolean;
}

export interface _Orders {
  /** * Разрешает оставить отзыв на заказ */
  reviewOrderEnabled: boolean;

  /** * Разрешает повторить заказ */
  repeatOrderEnabled: boolean;

  /** * Разрешает отменить заказ */
  cancelOrderEnabled: boolean;

  /** * Включает отслеживание заказа */
  orderTrackingEnabled: boolean;

  /** * Включает пагинацию истории заказов */
  orderHistoryPagination: boolean;

  /** * Показывает счетчик товаров на экране заказа */
  showOrderPageProductCardCounter: boolean;
}

export interface _Auth {
  /** * Включает авторизацию через Telegram */
  telegramAuthEnabled: boolean;

  /** * Разрешает использовать телефон или email для входа */
  phoneOrEmailFieldEnabled: boolean;

  /** * Показывает поле имени при регистрации */
  registrationNameFieldEnabled: boolean;

  /** * Username Telegram-бота для авторизации */
  telegramBotUsername: string;

  /** * Показывает поле фамилии при регистрации */
  registrationLastNameFieldEnabled: boolean;
}

export interface _Users {
  /** * Показывает поле квартиры при вводе адреса */
  showFlatDelivery: boolean;

  /** * Показывает поле номера телефона */
  showPhoneNumberInput: boolean;

  /** * Показывает список адресов пользователя */
  showCustomerAddresses: boolean;

  /** * Включает страницу настроек пользователя */
  isSettingsPageEnabled: boolean;

  /** * Разрешает удаление аккаунта через клиент */
  hasClientToDeleteUser: boolean;

  /** * Разрешает пользователю менять пароль */
  isUpdatePasswordEnabled: boolean;

  /** * Включает опрос пользователя */
  isCustomerSurveyEnabled: boolean;

  /** * Включает функциональность адресов пользователя */
  isCustomerAddressesEnabled: boolean;

  /** * Требует создать адрес перед использованием сервиса */
  isNecessaryToCreateAddress: boolean;

  /** * Разрешает закрыть опрос пользователя */
  allowDismissCustomerSurvey: boolean;

  /** * Показывает кнопку отправки обращения в поддержку */
  showSendCustomerRequestButton: boolean;

  /** * Меняет поля адреса в зависимости от способа доставки */
  addressInputDependsOnShippingMethod: boolean;

  /** * Ссылка на страницу удаления аккаунта */
  deleteAccountLink: string;
}

export interface _Delivery {
  /** * Доплата за превышение веса */
  extraWeightFee: number;

  /** * Вес, после которого применяется доплата */
  maxWeightForFeeKG: number;

  /** * Максимальный вес для экспресс-доставки */
  expressMaxWeightKg: number;

  maxWeightFeeStep: number;

  /** * Включает доплату за вес заказа */
  isWeightFeeEnabled: boolean;

  /** * Максимальный вес заказа */
  maxWeightForOrderKG: number;

  /** * ID зоны доставки по умолчанию */
  defaultShippingZoneId: string;

  /** * Включает выбор даты доставки */
  isDeliveryDateEnabled: boolean;

  /** * Использует статические данные доставки и оплаты */
  staticOrderDataEnabled: boolean;

  /** * Включает зоны доставки */
  isShippingZonesEnabled: boolean;

  /** * Показывает зону доставки по умолчанию */
  showDefaultShippingZone: boolean;

  /** * Конец доступного времени экспресс-доставки */
  expressAllowedTimeEnd: string;

  /** * Включает экспресс-доставку */
  isExpressDeliveryEnabled: boolean;

  /** * Включает выбор интервала доставки */
  isDeliveryIntervalEnabled: boolean;

  /** * Начало доступного времени экспресс-доставки */
  expressAllowedTimeStart: string;

  /** * Показывает выбор текущего адреса доставки */
  showCurrentShippingAddressSelect: boolean;

  /** * Показывает старую форму ввода адреса на экране доставки */
  showOldAddressInputOnShippingPage: boolean;

  /** * Доступные интервалы доставки */
  deliveryIntervals: DeliveryInterval[];

  /** * Заблокированные интервалы доставки */
  blockedDeliveryIntervals: BlockedDeliveryInterval[];

  /** * Доступные зоны доставки */
  shippingZones: _ShippingZone[];

  /** * Доступные способы доставки */
  shippingMethods: _ShippingMethod[];

  /** * Связи между зонами и способами доставки */
  shippingZoneMethods: _ShippingZoneMethod[];
}

export interface DeliveryInterval {
  /** * ID интервала доставки */
  id: string;

  /** * Текстовое значение интервала доставки */
  text: string;
}

export interface BlockedDeliveryInterval {
  /** * День недели, для которого заблокирован интервал */
  dayOfWeek: number;

  /** * ID заблокированного интервала доставки */
  deliveryIntervalId: string;
}

export interface _ShippingZone {
  /** * ID зоны доставки */
  id: string;

  /** * Название зоны доставки */
  name: string;
}

export interface _ShippingMethod {
  /** * ID записи способа доставки */
  id: string;

  /** * Стоимость доставки */
  cost: string;

  /** * ID способа доставки */
  methodId: string;

  /** * Включает способ доставки */
  enabled: boolean;

  /** * Название способа доставки */
  title: string;

  /** * Минимальная сумма заказа для способа доставки */
  minAmount: string;

  /** * Описание способа доставки */
  description: string;

  /** * Тип доставки */
  methodDeliveryType: string;

  /** * Код или вариант способа доставки */
  methodType: string;
}

export interface _ShippingZoneMethod {
  /** * ID связи зоны и способа доставки */
  id: number;

  /** * ID способа доставки */
  shippingMethodId: number;

  /** * ID зоны доставки */
  shippingZoneId: number;
}

export interface _Payments {
  /** * Включает платежный модуль */
  isPaymentsEnabled: boolean;

  /** * Включает онлайн-оплату */
  isOnlinePaymentEnabled: boolean;

  /** * Доступные способы оплаты */
  paymentMethods: PaymentMethod[];
}

export interface _Loyalty {
  /** * Включает карту лояльности */
  hasCardLoyalty: boolean;

  /** * Включает приглашение друзей */
  isInviteEnabled: boolean;

  /** * Включает реферальную программу */
  isReferralEnabled: boolean;

  /** * Включает бонусные баллы */
  isBonusPointsEnabled: boolean;

  /** * Показывает статистику рефералов */
  isReferralStatsEnabled: boolean;

  /** * Учитывает покупки приглашенных пользователей */
  isInviteePurchasesEnabled: boolean;

  /** * Описание механики приглашения друзей */
  inviteFriendDesc: string;

  /** * Условия начисления бонусов за приглашения */
  inviteFriendTerms: string;

  /** * TODO: проверить, какие именно карты включает */
  isCardsEnabled: boolean;
}

export interface _Notifications {
  /** * Включает email-push уведомления */
  isEmailPushEnabled: boolean;

  /** * Включает Firebase Cloud Messaging */
  isFCMNotificationsEnabled: boolean;

  /** * Включает страницу уведомлений */
  isNotificationsPageEnabled: boolean;

  /** * Включает локальные уведомления */
  isLocalNotificationsEnabled: boolean;

  /** * Задержка напоминания о корзине в минутах */
  cartRemindDelayMinutes: number;

  /** * Включает уведомления о заказах */
  isOrderNotificationsEnabled: boolean;

  /** * Включает push-уведомления о заказах */
  isOrderPushNotificationsEnabled: boolean;

  /** * Включает уведомления о скидках */
  isDiscountNotificationsEnabled: boolean;

  /** * Включает SMS-уведомления о скидках */
  isDiscountSmsNotificationsEnabled: boolean;

  /** * Включает push-уведомления о скидках */
  isDiscountPushNotificationsEnabled: boolean;

  /** * Включает email-уведомления о скидках */
  isDiscountEmailNotificationsEnabled: boolean;
}

export interface _Content {
  /** * Контакты для отображения в приложении */
  contacts: any[];

  /** * HTML-блок с информацией о компании */
  aboutCompanyHtml: string;

  /** * Показывает контентные блоки в футере */
  isContentInFooterShown: boolean;

  /** * Включает дополнительную контентную страницу */
  isAdditionalPageEnabled: boolean;

  /** * Заголовок для системного шаринга */
  sharedTitle: string;

  /** * Текст для системного шаринга */
  sharedText: string;

  /** * Ссылка на Android-приложение */
  sharedAndroidUrl: string;

  /** * Ссылка на iOS-приложение */
  sharedIosUrl: string;
}

export interface _Legal {
  /** * Реквизиты компании */
  requisites: any[];

  /** * Включает страницу оферты */
  isOffertaPageEnabled: boolean;

  /** * Ссылка на документ оферты */
  offertaUrl: string;

  /** * Ссылка на политику конфиденциальности */
  privacyPolicyPageLink: string;

  /** * ФИО директора компании */
  director: string;

  /** * ИНН и КПП компании */
  innKpp: string;

  /** * Юридический адрес компании */
  legalAddress: string;

  /** * Название юридического лица */
  orgName: string;

  /** * ОГРН компании */
  ogrn: string;
}

export interface _Integrations {
  /** * Включает интеграцию TaxiMaster */
  isTaxiMasterEnabled: boolean;

  /** * Включает интеграцию Wayset (альтернатива TaxiMaster) */
  isWaysetEnabled: boolean;

  /** * Автоподтверждение изменения цены Wayset, если разница не больше суммы (₽) */
  waysetQuoteAutoConfirmAmount?: number;

  /** * Автоподтверждение изменения цены Wayset, если разница не больше процента */
  waysetQuoteAutoConfirmPercent?: number;

  /** * Включает AppMetrica */
  isAppMetricaEnabled: boolean;

  /** * API-ключ AppMetrica */
  appMetricaApiKey: string;

  /** * Включает Telegram-интеграцию авторизации */
  isTelegramAuthEnabled: boolean;

  /** * Username Telegram-бота */
  telegramBotUsername: string;
}
